/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListEnvironmentsRequest,
  ListEnvironmentsSecurity,
} from "../models/operations/listenvironments.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListEnvironmentsQuery,
  ListEnvironmentsQueryData,
  prefetchListEnvironments,
  queryKeyListEnvironments,
} from "./listEnvironments.core.js";
export {
  buildListEnvironmentsQuery,
  type ListEnvironmentsQueryData,
  prefetchListEnvironments,
  queryKeyListEnvironments,
};

export type ListEnvironmentsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listEnvironments environments
 *
 * @remarks
 * List all environments for an organization
 */
export function useListEnvironments(
  request?: ListEnvironmentsRequest | undefined,
  security?: ListEnvironmentsSecurity | undefined,
  options?: QueryHookOptions<
    ListEnvironmentsQueryData,
    ListEnvironmentsQueryError
  >,
): UseQueryResult<ListEnvironmentsQueryData, ListEnvironmentsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListEnvironmentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listEnvironments environments
 *
 * @remarks
 * List all environments for an organization
 */
export function useListEnvironmentsSuspense(
  request?: ListEnvironmentsRequest | undefined,
  security?: ListEnvironmentsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListEnvironmentsQueryData,
    ListEnvironmentsQueryError
  >,
): UseSuspenseQueryResult<
  ListEnvironmentsQueryData,
  ListEnvironmentsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListEnvironmentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListEnvironmentsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListEnvironmentsQueryData,
): ListEnvironmentsQueryData | undefined {
  const key = queryKeyListEnvironments(...queryKeyBase);

  return client.setQueryData<ListEnvironmentsQueryData>(key, data);
}

export function invalidateListEnvironments(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "environments", "list", ...queryKeyBase],
  });
}

export function invalidateAllListEnvironments(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "environments", "list"],
  });
}
